import React from 'react';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }

    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}

var defaultFilterFn = function defaultFilterFn(options, searchValue) {
  return options.filter(function (option) {
    return option.value.toLowerCase().includes(searchValue.toLowerCase());
  }).sort(function (a, b) {
    return a.value.toLowerCase().indexOf(searchValue.toLowerCase());
  });
};

function useDebounce(fn, time) {
  if (time === void 0) {
    time = 0;
  }

  var ref = React.useRef(null);
  var fnRef = React.useRef();
  fnRef.current = fn;
  React.useEffect(function () {
    return function () {
      clearTimeout(ref.current);
    };
  }, [time]);
  return React.useCallback(_async(function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (ref.current) {
      clearTimeout(ref.current);
    }

    return new Promise(function (resolve, reject) {
      ref.current = setTimeout(function () {
        ref.current = null;

        try {
          resolve(fnRef.current.apply(fnRef, args));
        } catch (err) {
          reject(err);
        }
      }, time);
    });
  }), [time]);
}

var initialState = {
  searchValue: '',
  resolvedSearchValue: '',
  isOpen: false,
  highlightedIndex: 0
};
var actions = {
  setOpen: 'setOpen',
  setSearch: 'setSearch',
  highlightIndex: 'highlightIndex'
};

function useHoistedState(initialState, reducer) {
  var reducerRef = React.useRef();
  reducerRef.current = reducer;

  var _React$useState = React.useState(initialState),
      state = _React$useState[0],
      _setState = _React$useState[1];

  var setState = React.useCallback(function (updater, action) {
    if (!action) {
      throw new Error('An action type is required to update the state');
    }

    return _setState(function (old) {
      return reducerRef.current(old, updater(old), action);
    });
  }, [_setState]);
  return [state, setState];
}

function useSelect(_ref) {
  var multi = _ref.multi,
      create = _ref.create,
      _ref$getCreateLabel = _ref.getCreateLabel,
      getCreateLabel = _ref$getCreateLabel === void 0 ? function (d) {
    return "Use \"" + d + "\"";
  } : _ref$getCreateLabel,
      _ref$stateReducer = _ref.stateReducer,
      stateReducer = _ref$stateReducer === void 0 ? function (old, newState, action) {
    return newState;
  } : _ref$stateReducer,
      duplicates = _ref.duplicates,
      options = _ref.options,
      value = _ref.value,
      onChange = _ref.onChange,
      _ref$scrollToIndex = _ref.scrollToIndex,
      scrollToIndex = _ref$scrollToIndex === void 0 ? function () {} : _ref$scrollToIndex,
      _ref$shiftAmount = _ref.shiftAmount,
      shiftAmount = _ref$shiftAmount === void 0 ? 5 : _ref$shiftAmount,
      _ref$filterFn = _ref.filterFn,
      filterFn = _ref$filterFn === void 0 ? defaultFilterFn : _ref$filterFn,
      optionsRef = _ref.optionsRef,
      _ref$getDebounce = _ref.getDebounce,
      getDebounce = _ref$getDebounce === void 0 ? function (options) {
    return options.length > 10000 ? 1000 : options.length > 1000 ? 200 : 0;
  } : _ref$getDebounce;

  var _useHoistedState = useHoistedState(initialState, stateReducer),
      _useHoistedState$ = _useHoistedState[0],
      searchValue = _useHoistedState$.searchValue,
      resolvedSearchValue = _useHoistedState$.resolvedSearchValue,
      isOpen = _useHoistedState$.isOpen,
      highlightedIndex = _useHoistedState$.highlightedIndex,
      setState = _useHoistedState[1]; // Refs


  var inputRef = React.useRef();
  var onBlurRef = React.useRef({});
  var onChangeRef = React.useRef();
  var filterFnRef = React.useRef();
  var getCreateLabelRef = React.useRef();
  var scrollToIndexRef = React.useRef();
  filterFnRef.current = filterFn;
  scrollToIndexRef.current = scrollToIndex;
  getCreateLabelRef.current = getCreateLabel;
  onChangeRef.current = onChange; // We need to memoize these default values to keep things
  // from rendereing without cause

  var defaultMultiValue = React.useMemo(function () {
    return [];
  }, []);
  var defaultOptions = React.useMemo(function () {
    return [];
  }, []); // Multi should always at least have an empty array as the value

  if (multi && typeof value === 'undefined') {
    value = defaultMultiValue;
  } // If no options are provided, then use an empty array


  if (!options) {
    options = defaultOptions;
  }

  var originalOptions = options; // If multi and duplicates aren't allowed, filter out the
  // selected options from the options list

  options = React.useMemo(function () {
    if (multi && !duplicates) {
      return options.filter(function (d) {
        return !value.includes(d.value);
      });
    }

    return options;
  }, [options, value, duplicates, multi]); // Compute the currently selected option(s)

  var selectedOption = React.useMemo(function () {
    if (!multi) {
      return originalOptions.find(function (d) {
        return d.value === value;
      }) || {
        label: value,
        value: value
      };
    } else {
      return value.map(function (val) {
        return originalOptions.find(function (d) {
          return d.value === val;
        }) || {
          label: val,
          value: val
        };
      });
    }
  }, [multi, value, originalOptions]); // If there is a search value, filter the options for that value
  // TODO: This is likely where we will perform async option fetching
  // in the future.

  options = React.useMemo(function () {
    if (resolvedSearchValue) {
      return filterFnRef.current(options, resolvedSearchValue);
    }

    return options;
  }, [options, resolvedSearchValue]); // If in create mode and we have a search value, fabricate
  // an option for that searchValue and prepend it to options

  options = React.useMemo(function () {
    if (create && searchValue) {
      return [{
        label: getCreateLabelRef.current(searchValue),
        value: searchValue
      }].concat(options);
    }

    return options;
  }, [create, searchValue, options]); // Actions

  var setOpen = React.useCallback(function (newIsOpen) {
    setState(function (old) {
      return _extends({}, old, {
        isOpen: newIsOpen
      });
    }, actions.setOpen);
  }, [setState]);
  var setResolvedSearch = useDebounce(function (value) {
    setState(function (old) {
      return _extends({}, old, {
        resolvedSearchValue: value
      });
    }, actions.setSearch);
  }, getDebounce(options));
  var setSearch = React.useCallback(function (value) {
    setState(function (old) {
      return _extends({}, old, {
        searchValue: value
      });
    }, actions.setSearch);
    setResolvedSearch(value);
  }, [setState, setResolvedSearch]);
  var highlightIndex = React.useCallback(function (value) {
    setState(function (old) {
      return _extends({}, old, {
        highlightedIndex: Math.min(Math.max(0, typeof value === 'function' ? value(old.highlightedIndex) : value), options.length - 1)
      });
    }, actions.highlightIndex);
  }, [options, setState]);
  var selectIndex = React.useCallback(function (index) {
    var option = options[index];

    if (option) {
      if (!multi) {
        onChangeRef.current(option.value);
      } else {
        if (duplicates || !value.includes(option.value)) {
          onChangeRef.current([].concat(value, [option.value]), option.value);
        }
      }
    }

    if (!multi) {
      setOpen(false);
    } else {
      setSearch('');
    }
  }, [multi, options, duplicates, value, setOpen, setSearch]);
  var removeValue = React.useCallback(function (index) {
    onChangeRef.current(value.filter(function (d, i) {
      return i !== index;
    }));
  }, [value]); // Handlers

  var handleSearchValueChange = function handleSearchValueChange(e) {
    setSearch(e.target.value);
    setOpen(true);
  };

  var handleSearchClick = function handleSearchClick() {
    if (!create || multi) {
      setSearch('');
    }

    setOpen(true);
  };

  var handleSearchFocus = function handleSearchFocus() {
    return handleSearchClick();
  }; // Prop Getters


  var ArrowUp = function ArrowUp(defaultShift, defaultMeta) {
    return function (_ref2, e) {
      var shift = _ref2.shift,
          meta = _ref2.meta;
      e.preventDefault();
      var amount = defaultMeta || meta ? 1000000000000 : defaultShift || shift ? shiftAmount - 1 : 1;
      setOpen(true);
      highlightIndex(function (old) {
        return old - amount;
      });
    };
  };

  var ArrowDown = function ArrowDown(defaultShift, defaultMeta) {
    return function (_ref3, e) {
      var shift = _ref3.shift,
          meta = _ref3.meta;
      e.preventDefault();
      var amount = defaultMeta || meta ? 1000000000000 : defaultShift || shift ? shiftAmount - 1 : 1;
      setOpen(true);
      highlightIndex(function (old) {
        return old + amount;
      });
    };
  };

  var Enter = function Enter(_, e) {
    if (isOpen) {
      if (searchValue || options[highlightedIndex]) {
        e.preventDefault();
      }

      if (options[highlightedIndex]) {
        selectIndex(highlightedIndex);
      }
    }
  };

  var Escape = function Escape() {
    setOpen(false);
  };

  var Tab = function Tab() {
    setOpen(false);
  };

  var Backspace = function Backspace() {
    if (!multi || searchValue) {
      return;
    }

    removeValue(value.length - 1);
  };

  var getKeyProps = useKeys({
    ArrowUp: ArrowUp(),
    ArrowDown: ArrowDown(),
    PageUp: ArrowUp(true),
    PageDown: ArrowDown(true),
    Home: ArrowUp(false, true),
    End: ArrowDown(false, true),
    Enter: Enter,
    Escape: Escape,
    Tab: Tab,
    Backspace: Backspace
  });

  var getInputProps = function getInputProps(_temp) {
    var _extends2;

    var _ref4 = _temp === void 0 ? {} : _temp,
        _ref4$refKey = _ref4.refKey,
        refKey = _ref4$refKey === void 0 ? 'ref' : _ref4$refKey,
        ref = _ref4.ref,
        _onChange = _ref4.onChange,
        _onFocus = _ref4.onFocus,
        _onClick = _ref4.onClick,
        _onBlur = _ref4.onBlur,
        rest = _objectWithoutPropertiesLoose(_ref4, ["refKey", "ref", "onChange", "onFocus", "onClick", "onBlur"]);

    return getKeyProps(_extends((_extends2 = {}, _extends2[refKey] = function (el) {
      inputRef.current = el;

      if (ref) {
        ref.current = el;
      }
    }, _extends2.value = (isOpen ? searchValue : selectedOption ? selectedOption.label : '') || '', _extends2.onChange = function onChange(e) {
      handleSearchValueChange(e);

      if (_onChange) {
        _onChange(e);
      }
    }, _extends2.onFocus = function onFocus(e) {
      handleSearchFocus();

      if (_onFocus) {
        _onFocus(e);
      }
    }, _extends2.onClick = function onClick(e) {
      handleSearchClick();

      if (_onClick) {
        _onClick(e);
      }
    }, _extends2.onBlur = function onBlur(e) {
      if (_onBlur) {
        e.persist();
        onBlurRef.current.cb = _onBlur;
        onBlurRef.current.event = e;
      }
    }, _extends2), rest));
  };

  var getOptionProps = function getOptionProps(_temp2) {
    var _ref5 = _temp2 === void 0 ? {} : _temp2,
        index = _ref5.index,
        _ref5$key = _ref5.key,
        key = _ref5$key === void 0 ? index : _ref5$key,
        _onClick2 = _ref5.onClick,
        _onMouseEnter = _ref5.onMouseEnter,
        rest = _objectWithoutPropertiesLoose(_ref5, ["index", "key", "onClick", "onMouseEnter"]);

    if (typeof index !== 'number' || index < 0) {
      throw new Error("useSelect: The getOptionProps prop getter requires an index property, eg. 'getOptionProps({index: 1})'");
    }

    return _extends({
      key: key
    }, rest, {
      onClick: function onClick(e) {
        selectIndex(index);

        if (_onClick2) {
          _onClick2(e);
        }
      },
      onMouseEnter: function onMouseEnter(e) {
        highlightIndex(index);

        if (_onMouseEnter) {
          _onMouseEnter(e);
        }
      }
    });
  }; // Effects
  // When the user clicks outside of the options box
  // while open, we need to close the dropdown


  useClickOutsideRef(isOpen, function () {
    setOpen(false);
  }, optionsRef); // When searching, activate the first option

  React.useEffect(function () {
    highlightIndex(0);
  }, [searchValue, highlightIndex]); // When we open and close the options, set the highlightedIndex to 0

  React.useEffect(function () {
    highlightIndex(0);

    if (!isOpen && onBlurRef.current.event) {
      onBlurRef.current.cb(onBlurRef.current.event);
      onBlurRef.current.event = null;
    }
  }, [isOpen, highlightIndex]); // When the highlightedIndex changes, scroll to that item

  React.useEffect(function () {
    scrollToIndexRef.current(highlightedIndex);
  }, [highlightedIndex]);
  React.useEffect(function () {
    if (isOpen) {
      setTimeout(function () {
        var _inputRef$current;

        if ((_inputRef$current = inputRef.current) == null ? void 0 : _inputRef$current.focus) {
          inputRef.current.focus();
        }
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [isOpen, inputRef.current]);
  return {
    // State
    searchValue: searchValue,
    isOpen: isOpen,
    highlightedIndex: highlightedIndex,
    selectedOption: selectedOption,
    visibleOptions: options,
    // Actions
    selectIndex: selectIndex,
    removeValue: removeValue,
    setOpen: setOpen,
    setSearch: setSearch,
    highlightIndex: highlightIndex,
    // Prop Getters
    getInputProps: getInputProps,
    getOptionProps: getOptionProps
  };
}

useSelect.actions = actions;

function useClickOutsideRef(enable, fn, userRef) {
  var localRef = React.useRef();
  var fnRef = React.useRef();
  fnRef.current = fn;
  var elRef = userRef || localRef;
  var handle = React.useCallback(function (e) {
    var isTouch = e.type === 'touchstart';

    if (e.type === 'click' && isTouch) {
      return;
    }

    var el = elRef.current;
    if (el && !el.contains(e.target)) fnRef.current(e);
  }, [elRef]);
  React.useEffect(function () {
    if (enable) {
      document.addEventListener('touchstart', handle, true);
      document.addEventListener('click', handle, true);
    }

    return function () {
      document.removeEventListener('touchstart', handle, true);
      document.removeEventListener('click', handle, true);
    };
  }, [enable, handle]);
}

var useKeys = function useKeys(userKeys) {
  return function (_temp3) {
    var _ref6 = _temp3 === void 0 ? {} : _temp3,
        _onKeyDown = _ref6.onKeyDown,
        rest = _objectWithoutPropertiesLoose(_ref6, ["onKeyDown"]);

    return _extends({}, rest, {
      onKeyDown: function onKeyDown(e) {
        var keyCode = e.keyCode,
            key = e.key,
            shift = e.shiftKey,
            meta = e.metaKey;
        var handler = userKeys[key] || userKeys[keyCode];

        if (handler) {
          handler({
            keyCode: keyCode,
            key: key,
            shift: shift,
            meta: meta
          }, e);
        }

        if (_onKeyDown) {
          _onKeyDown(e);
        }
      }
    });
  };
};

export default useSelect;